import React, { useEffect, useMemo, useState } from 'react';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';
import classNames from 'classnames';
import moment from 'moment/moment';
import SankeyChart from 'shared/components/dashboardCharts/SankeyChart/SankeyChart.tsx';
import HeaderPanel from './HeaderPanel.tsx';
import { PANELS, PANELS_TYPES } from 'app/containers/MainDashboard/consts';
import {
  AWS_GROUP_BY_LOV,
  AZURE_GROUP_BY_LOV,
  GCP_GROUP_BY_LOV,
  GROUP_BY_LOV,
  GroupByLovToAttributes,
  MULTI_GROUP_BY_LOV,
} from 'usage/constants/costAndUsageConstants';
import { CLOUD_TYPE_IDS } from 'users/constants/usersConstants';
import { useRootStore } from 'app/contexts/RootStoreContext';
import { useMainDashboardContext } from 'app/containers/MainDashboard/contexts/mainDashboardContext';
import { getFirstDayOfMonth } from 'app/containers/MainDashboard/utils';
import { getGroupOptions } from 'app/containers/MainDashboard/tsUtils';
import DateFilter from 'shared/modules/dateFilter';
import useCueData from 'app/containers/MainDashboard/hooks/react-query/useCueData.js';
import {
  CueDataItem,
  FinalDataItem,
  GroupByItem,
  GroupByOption,
  LinkItem,
  MtdCostBreakdownProps,
  SankeyNode,
} from './types.ts';
import mainStyles from '../../mainDashboardCommon.module.scss';
import styles from './mtdCostBreakdown.module.scss';

const colors = ['#84d9fc', '#c19bfe', '#989bfa', '#74adee', '#98c6fa', '#f688dd', '#d8b6ea', '#8be9de'];
const linkedAccount = 'linkedaccid';
const getFirstGroupDefault = (usersStore, options) => {
  const { isCurrentAccountSingle, isCurrentAccountMulticloud } = usersStore;
  if (isCurrentAccountMulticloud) {
    return [options.find((a) => a.value === GroupByLovToAttributes.get(MULTI_GROUP_BY_LOV.BY_CLOUD))];
  }
  return isCurrentAccountSingle
    ? [options.find((a) => a.value === GroupByLovToAttributes.get(AWS_GROUP_BY_LOV.BY_SERVICE))]
    : [options.find((a) => a.value === GroupByLovToAttributes.get(GROUP_BY_LOV.PAYER_ACCOUNT))];
};
const getSecondGroupDefault = (usersStore, options) => {
  const {
    isCurrentAccountSingle,
    isCurrentAccountMulticloud,
    currDispUserCloudAccountType: currentCloudType,
  } = usersStore;
  if (isCurrentAccountMulticloud) {
    return [options.find((a) => a.value === GroupByLovToAttributes.get(GROUP_BY_LOV.PAYER_ACCOUNT))];
  }
  if (!isCurrentAccountSingle) {
    return [options.find((a) => a.value === GroupByLovToAttributes.get(GROUP_BY_LOV.BY_SERVICE))];
  }
  switch (currentCloudType) {
    case CLOUD_TYPE_IDS.AWS:
      return [options.find((a) => a.value === GroupByLovToAttributes.get(AWS_GROUP_BY_LOV.BY_LINKED_ACC_ID))];
    case CLOUD_TYPE_IDS.AZURE:
      return [options.find((a) => a.value === GroupByLovToAttributes.get(AZURE_GROUP_BY_LOV.BY_SUBSCRIPTION_ID))];
    case CLOUD_TYPE_IDS.GCP:
      return [options.find((a) => a.value === GroupByLovToAttributes.get(GCP_GROUP_BY_LOV.BY_LINKED_ACC_ID))];
  }
  return [options.find((a) => a.value === GroupByLovToAttributes.get(AWS_GROUP_BY_LOV.BY_LINKED_ACC_ID))];
};

const MtdCostBreakdown: React.FC<MtdCostBreakdownProps> = ({
  hideSaveDashboard,
  existingFirstGroupBy,
  existingSecondGroupBy,
  accountKey,
  divisionId,
  userKey,
  overrideCurrency,
}) => {
  const { usersStore, appStore } = useRootStore();
  const withOverrideApiKey = {
    rootStore: { usersStore, appStore },
    accountKey,
    divisionId,
    userKey,
  };
  const { updatePanelLoadingState, getDynamicFilters, getCurrentDay, getOverrideAccount } = useMainDashboardContext();
  const options: GroupByOption[] = getGroupOptions(usersStore, accountKey ? getOverrideAccount(accountKey) : null).map(
    (option) => ({
      value: option.value,
      label: option.label ?? '',
    }),
  );
  const [groupByFirst, setGroupByFirst] = useState<GroupByItem[]>(
    existingFirstGroupBy ?? getFirstGroupDefault(usersStore, options),
  );
  const [groupBySecond, setGroupBySecond] = useState<GroupByItem[]>(
    existingSecondGroupBy ?? getSecondGroupDefault(usersStore, options),
  );
  const cueHook = useCueData(!appStore.isChangeAccountLoading);

  const mtdCostBreakdownParams = {
    ...PANELS[PANELS_TYPES.COST_BREAKDOWN].params,
    start: getFirstDayOfMonth(moment(DateFilter.getDate())),
    end: moment(getCurrentDay(accountKey)).format('YYYY-MM-DD'),
    ...getDynamicFilters(),
    isPpApplied: appStore.isPpApplied,
    groupBy: groupByFirst[0]?.value,
    secondaryGroupBy: groupBySecond[0]?.value,
    withOverrideApiKey,
  };

  const { data: cueData, isLoading } = cueHook.fetchCueData(mtdCostBreakdownParams) as {
    data: CueDataItem[] | undefined;
    isLoading: boolean;
  };

  useEffect(() => {
    updatePanelLoadingState(PANELS_TYPES.COST_BREAKDOWN, isLoading);
  }, [isLoading, updatePanelLoadingState]);

  const isFirstGroupLinkedAccount = groupByFirst[0]?.value === linkedAccount;
  const isSecondGroupLinkedAccount = groupBySecond[0]?.value === linkedAccount;

  const cueTransferData = useMemo(() => {
    if (!cueData) {
      return;
    }
    //group and sum by `group_by` and `group_by_secondary`
    const groupByMap = new Map();
    cueData.forEach(({ group_by, total_cost, linked_account_name }) => {
      const groupField = isFirstGroupLinkedAccount ? linked_account_name : group_by;
      if (!groupByMap.has(groupField)) {
        groupByMap.set(groupField, 0);
      }
      groupByMap.set(groupField, groupByMap.get(groupField) + total_cost);
    });
    const secondGroupByMap = new Map();
    cueData.forEach(({ group_by_secondary, total_cost, linked_account_name }) => {
      const groupSecondaryField = isSecondGroupLinkedAccount ? linked_account_name : group_by_secondary;
      if (!secondGroupByMap.has(groupSecondaryField)) {
        secondGroupByMap.set(groupSecondaryField, 0);
      }
      secondGroupByMap.set(groupSecondaryField, secondGroupByMap.get(groupSecondaryField) + total_cost);
    });

    //Sort and take the top 5 `group_by`
    const sortedGroupBy = Array.from(groupByMap.entries())
      .filter((entry) => entry[0] !== 'Other')
      .sort((a, b) => b[1] - a[1]);
    const top5GroupBy = sortedGroupBy.slice(0, 5).map(([group_by]: [string, number]) => group_by);
    const sortedGroupBySecondary = Array.from(secondGroupByMap.entries())
      .filter((entry) => entry[0] !== 'Other')
      .sort((a, b) => b[1] - a[1]);
    const top5GroupBySecondary = sortedGroupBySecondary.slice(0, 5).map(([group_by]: [string, number]) => group_by);

    // Step 3: Prepare final data
    const finalData: FinalDataItem[] = [];
    top5GroupBy.forEach((group_by) => {
      const secondaryMap = new Map();
      // Group and sum by `group_by_secondary` for the current `group_by`
      cueData
        .filter((item) => group_by === (isFirstGroupLinkedAccount ? item.linked_account_name : item.group_by))
        .forEach(({ group_by_secondary, linked_account_name, total_cost }) => {
          const groupSecondaryField = isSecondGroupLinkedAccount ? linked_account_name : group_by_secondary;

          if (top5GroupBySecondary.includes(groupSecondaryField)) {
            if (!secondaryMap.has(groupSecondaryField)) {
              secondaryMap.set(groupSecondaryField, 0);
            }
            secondaryMap.set(groupSecondaryField, secondaryMap.get(groupSecondaryField) + total_cost);
          } else {
            secondaryMap.set('Others', (secondaryMap.get('Others') ?? 0) + total_cost);
          }
        });

      secondaryMap.forEach((total_cost, group_by_secondary) => {
        finalData.push({ group_by, group_by_secondary, total_cost });
      });
    });

    const otherGroups = cueData.filter(({ group_by, linked_account_name }) =>
      isFirstGroupLinkedAccount ? !top5GroupBy.includes(linked_account_name) : !top5GroupBy.includes(group_by),
    );
    const otherGroupBySecondaryTotals = new Map<string, number>();

    otherGroups.forEach(({ group_by_secondary, linked_account_name, total_cost }) => {
      const groupSecondaryField = isSecondGroupLinkedAccount ? linked_account_name : group_by_secondary;

      if (top5GroupBySecondary.includes(groupSecondaryField)) {
        otherGroupBySecondaryTotals.set(
          groupSecondaryField,
          (otherGroupBySecondaryTotals.get(groupSecondaryField) ?? 0) + total_cost,
        );
      } else {
        otherGroupBySecondaryTotals.set('Others', (otherGroupBySecondaryTotals.get('Others') ?? 0) + total_cost);
      }
    });

    otherGroupBySecondaryTotals.forEach((total_cost, group_by_secondary) => {
      finalData.push({
        group_by: 'Others',
        group_by_secondary,
        total_cost,
      });
    });

    return finalData;
  }, [cueData, isFirstGroupLinkedAccount, isSecondGroupLinkedAccount]);

  const totalCost = useMemo(() => {
    return {
      label: 'Total Cost',
      value: cueData ? cueData.reduce((acc, item) => acc + item.total_cost, 0) : 0,
    };
  }, [cueData]);

  const chartData = useMemo(() => {
    if (!cueTransferData) {
      return { nodes: [], links: [] };
    }

    const sourceNodes: SankeyNode[] = [{ name: '', color: '#99DFF9', id: 'main_total_cost' }];
    const targetNodes: SankeyNode[] = [];
    const links: LinkItem[] = [];
    const sourceNodeMap = new Map();
    const targetNodeMap = new Map();
    let colorIndex = 0;

    // Create unique nodes
    cueTransferData.forEach(({ group_by }) => {
      if (!sourceNodeMap.has(group_by)) {
        sourceNodeMap.set(group_by, sourceNodes.length);
        sourceNodes.push({ name: group_by, id: `source-${group_by}`, color: colors[colorIndex] });
        colorIndex = colorIndex < colors.length - 1 ? colorIndex + 1 : 0;
      }
    });
    cueTransferData.forEach(({ group_by_secondary }) => {
      if (!targetNodes.find((node) => node.name === group_by_secondary)) {
        targetNodes.push({ name: group_by_secondary, id: `target-${group_by_secondary}`, color: colors[colorIndex] });
        colorIndex = colorIndex < colors.length - 1 ? colorIndex + 1 : 0;
      }
    });
    const targetNodeTotals = new Map<string, number>();
    cueTransferData.forEach(({ group_by_secondary, total_cost }) => {
      targetNodeTotals.set(group_by_secondary, (targetNodeTotals.get(group_by_secondary) ?? 0) + total_cost);
    });
    const othersNode = targetNodes.find((node) => node.name === 'Others');
    const sortedTargetNodes = targetNodes
      .filter((node) => node.name !== 'Others')
      .sort((a, b) => (targetNodeTotals.get(b.name) ?? 0) - (targetNodeTotals.get(a.name) ?? 0));
    if (othersNode) {
      sortedTargetNodes.push(othersNode);
    }
    sortedTargetNodes.forEach((node, index) => {
      targetNodeMap.set(node.name, index);
    });
    targetNodes.sort((a, b) => (targetNodeMap.get(a.name) ?? 0) - (targetNodeMap.get(b.name) ?? 0));

    //add all first groupBy to be under total cost main node
    cueTransferData.forEach(({ group_by }) => {
      const groupByIndex = sourceNodeMap.get(group_by);
      if (!links.find((l) => l.target === groupByIndex)) {
        const totalValue = cueTransferData
          .filter((item) => item.group_by === group_by)
          .reduce((acc, item) => acc + item.total_cost, 0);
        if (totalValue !== 0) {
          links.push({
            source: 0,
            target: groupByIndex,
            value: totalValue,
          });
        }
      }
    });

    //Create links and check for circular references
    cueTransferData.forEach(({ group_by, group_by_secondary, total_cost }) => {
      const sourceIndex = sourceNodeMap.get(group_by);
      const targetIndex = targetNodeMap.get(group_by_secondary);

      if (sourceIndex !== targetIndex + sourceNodes.length && total_cost !== 0) {
        links.push({
          source: sourceIndex,
          target: targetIndex + sourceNodes.length,
          value: total_cost,
        });
      }
    });

    return {
      nodes: links?.length > 0 ? [...sourceNodes, ...targetNodes] : [],
      links,
    };
  }, [cueTransferData]);

  return (
    <div className={classNames(mainStyles.panelWrapper, mainStyles.fullWidth, styles.minHeight)}>
      <HeaderPanel
        groupByFirst={groupByFirst}
        setGroupByFirst={setGroupByFirst}
        groupBySecond={groupBySecond}
        setGroupBySecond={setGroupBySecond}
        options={options}
        hideSaveDashboard={hideSaveDashboard}
        navigateCueParams={mtdCostBreakdownParams}
      />
      {isLoading ? (
        <Spinner size={SPINNER_SIZES.MEDIUM} />
      ) : (
        <div className={styles.chartWrapper}>
          <SankeyChart overrideCurrency={overrideCurrency} data={chartData} mainNode={totalCost} />
        </div>
      )}
    </div>
  );
};

export default MtdCostBreakdown;
